import React, {useContext, useEffect, useState} from 'react'
import PropTypes from "prop-types";
import {FormattedMessage, injectIntl} from "react-intl";
import {BeatLoader} from "react-spinners";
import {Button} from "@mui/material";
import {withStyles} from "@mui/styles";
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { ReactComponent as BrushColor } from 'assets/img/brush_color.svg'

import showStyle from "assets/jss/views/picking/order/showStyle";
import HeaderActionButton from "./HeaderActionButton";
import GivenToCustomerButton from "./GivenToCustomerButton";
import TrackingNumbers from "./TrackingNumbers";

import {fourteenthGrey, orangeColor, primaryColor} from "assets/jss/main";

import {OrderContext} from "contexts/OrderContext";
import {ShippingMethods} from "utils/ShippingMethods";
import ShipmentTitle from "./ShipmentTitle";
import {ShipmentStatus} from 'utils/ShipmentStatus';
import {LineItemStatus} from 'utils/LineItemStatus';
import {sortOn} from "../../../../../../utils/sortOn";

function ShipmentHeader({classes, shipment, incrementalNumber, lineItems, currentOrganization, displayShipmentCheckbox, setDisplayShipmentCheckbox}) {

    const {
        setLineItemSelectedIds,
        toggleModalWaybillSelector,
        setSelectedShipment,
        selectedShipment,
        waitingTimeBeforeRefreshingList,
        lineItemSelectedIds,
        openShipmentAddressEditModal,
    } = useContext(OrderContext);

    const canEditShipmentAddress = shipment?.status !== ShipmentStatus.SHIPPED;

    const [associatedReturnShipments, setAssociatedReturnShipments] = useState([]);
    const [isAddressExpanded, setIsAddressExpanded] = useState(false);

    const {
        shippedAt,
        trackingNumber,
        error,
        shippingMethod,
        status,
    } = shipment;

    const isPreShipment = status !== ShipmentStatus.SHIPPED;
    const pendingShipmentLabel = !error && !trackingNumber && shippedAt === null && !isPreShipment;
    const showRegenerateButton =
        (error
            && !trackingNumber
            && shippedAt === null
            && !waitingTimeBeforeRefreshingList)

    useEffect(() => {
        setAssociatedReturnShipments(getReturnShipmentThatContainShipmentLineItems());
    }, [shipment, lineItems]);

    function toggleAddress() {
        setIsAddressExpanded(!isAddressExpanded);
    }

    function renderDeliveryAddress() {
        const addressIndent = displayShipmentCheckbox ? 42 : 0;

        const companyLine = shipment.deliveryCompany;

        const fullName = [shipment.deliveryFirstname, shipment.deliveryLastname]
            .filter(Boolean)
            .join(' ');

        const cityCountryLine = [shipment.deliveryPostalCode, shipment.deliveryCity, shipment.deliveryCountry]
            .filter(Boolean)
            .join(' ');

        return (
            <div
                style={{
                    marginLeft: `${addressIndent}px`,
                    padding: '8px 12px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '4px',
                    fontSize: '12px',
                    lineHeight: '1.6',
                    color: fourteenthGrey,
                }}
            >
                {companyLine && <div>{companyLine}</div>}

                {fullName && (
                    <div style={{display: 'flex', alignItems: 'center'}}>
                        <span>{fullName}</span>
                        {canEditShipmentAddress && (
                            <BrushColor
                                style={{marginLeft: '6px', cursor: 'pointer', width: '14px', height: '14px'}}
                                onClick={() => openShipmentAddressEditModal(shipment)}
                            />
                        )}
                    </div>
                )}

                {shipment.deliveryAddress && <div>{shipment.deliveryAddress}</div>}
                {shipment.deliveryAddressMore && <div>{shipment.deliveryAddressMore}</div>}
                {cityCountryLine && <div>{cityCountryLine}</div>}
                {shipment.deliveryPhone && (
                    <div>
                        <FormattedMessage id="picking.order.shipments.header.deliveryAddress.phone" defaultMessage="Tel" />: {shipment.deliveryPhone}
                    </div>
                )}
                {shipment.deliveryEmail && (
                    <div>
                        <FormattedMessage id="picking.order.shipments.header.deliveryAddress.email" defaultMessage="Email" />: {shipment.deliveryEmail}
                    </div>
                )}
            </div>
        );
    }

    function canGivenToCustomer() {
        if (selectedShipment?.id !== shipment.id && !atLeastOneItemSelected()) {
            return false;
        }

        const hasItemToShip = lineItems.some((item) => item.status === LineItemStatus.TO_SHIP);

        return shipment && shippedAt === null && isClickAndCollect() && hasItemToShip;
    }

    function isClickAndCollect() {
        return shippingMethod.type === ShippingMethods.CLICK_COLLECT
            || shippingMethod.type === ShippingMethods.DRIVE
            || shippingMethod.type === ShippingMethods.LOCKER;
    }

    /**
     * Check if at least one item from the shipment is selected
     */
    function atLeastOneItemSelected() {
        return lineItems.some((item) => {
            return lineItemSelectedIds.includes(item['@id']);
        });
    }

    function renderShipButton() {
        return (
            <Button
                color="primary"
                variant="contained"
                classes={{root: classes.justificationButton}}
                disabled={atLeastOneItemSelected() === false}
                onClick={() => toggleModalWaybillSelector()}
            >
                <FormattedMessage id={`picking.order.shipments.header.action.shipSelected`}/>
            </Button>
        );
    }

    function renderGivenToCustomerButton() {
        return (
            <GivenToCustomerButton
                shipment={shipment}
                disabled={canGivenToCustomer() === false}
            >
                <FormattedMessage id={`picking.order.shipments.header.action.givenToCustomer`} />
            </GivenToCustomerButton>
        );
    }

    function displayShipActions() {
        const wrongShipmentStatus = shipment.status === ShipmentStatus.SHIPPED;
        const noItemToShip = lineItems.every((item) => {
            return item.status !== LineItemStatus.TO_SHIP;
        });

        const allItemAreHandledByWMS = lineItems.every((item) => item['wmsReady'] === true);
        const allItemAreHandledBySAP = lineItems.every((item) => item['customMetaData'] && item['customMetaData']['isSAP'] === true);
        const isSupplierShippingMethod = 'custom' === lineItems[0].shippingMethod.type;
        const allItemAreHandledBySAPAndAreNotCVAndSupplierShipment = allItemAreHandledBySAP && isSupplierShippingMethod && lineItems.every((item) =>
            item && item['customMetaData'] && item['customMetaData']['isCv'] === true
        );

        if (error || noItemToShip || wrongShipmentStatus) {
            return null;
        }

        if (isClickAndCollect() && trackingNumber) {
            return renderGivenToCustomerButton();
        }

        /** TODO once WMS is merged in prod, uncomment this code **/
        if (allItemAreHandledByWMS) {
            return null;
        }
        /*
        if (allItemAreHandledByWMS || (allItemAreHandledBySAP && !allItemAreHandledBySAPAndAreNotCVAndSupplierShipment)) {
            return null;
        }
        */

        return renderShipButton();
    }

    function getReturnShipmentThatContainShipmentLineItems() {
        let matchReturnShipments = [];

        // new system: return is a new shipment
        lineItems.forEach((item) => {
            if (item.returnShipments.length > 0) {
                matchReturnShipments.push(item.returnShipments[0]); // lineItem can be only in one return shipment
            }
        })

        // legacy system: return infos are in the current shipment
        if (shipment.returnTrackingNumber) {
            matchReturnShipments.push(shipment);
        }

        const uniqueIds = new Set();

        // array unique
        matchReturnShipments = matchReturnShipments.filter(shipment => {
            if (!shipment.id) return true; // fallback: include if no id

            if (uniqueIds.has(shipment.id)) {
                return false;
            }

            uniqueIds.add(shipment.id);

            return true;
        });

        return matchReturnShipments.sort(sortOn('createdAt'));
    }

    return (
        <div className={classes.header}>
            <div className={classes.headerMainRow}>
                <ShipmentTitle
                    incrementalNumber={incrementalNumber}
                    shipment={shipment}
                    lineItems={lineItems}
                    displayShipmentCheckbox={displayShipmentCheckbox}
                    setDisplayShipmentCheckbox={setDisplayShipmentCheckbox}
                    isAddressExpanded={isAddressExpanded}
                    toggleAddress={toggleAddress}
                />
                {pendingShipmentLabel && (
                    <span className={classes.pendingShipmentLabel}>
                        <FormattedMessage id={`picking.order.shipments.header.action.regenerate.pending`} />
                    </span>
                )}
                {waitingTimeBeforeRefreshingList && (
                    <BeatLoader
                        sizeUnit={"px"}
                        size={12}
                        color={primaryColor}
                        loading={true}
                    />
                )}
                {displayShipActions()}
                {(showRegenerateButton || error) && (
                    <>
                        {error &&
                            <div className={classes.errorContainer}>
                                <ErrorOutlineIcon className={classes.errorIcon} htmlColor={orangeColor} />
                                <div>
                                    <span className={classes.error}>{error}</span>
                                </div>
                            </div>
                        }
                        {showRegenerateButton &&
                            <HeaderActionButton
                                onClick={() => {
                                    setSelectedShipment(shipment);
                                    setLineItemSelectedIds(lineItems.map(li => li['@id']));
                                    toggleModalWaybillSelector();
                                }}
                            >
                                <FormattedMessage id={`picking.order.shipments.header.action.regenerate`} />
                            </HeaderActionButton>
                        }
                    </>
                )}
                {!pendingShipmentLabel && !waitingTimeBeforeRefreshingList && (
                    <div className={classes.rightPart}>
                        <TrackingNumbers
                            trackingNumber={trackingNumber}
                            returnShipments={associatedReturnShipments}
                            shipment={shipment}
                            currentOrganization={currentOrganization}
                        />
                    </div>
                )}
            </div>
            {isAddressExpanded && (
                <div className={classes.headerAddressRow}>
                    {renderDeliveryAddress()}
                </div>
            )}
        </div>
    )
}

ShipmentHeader.defaultProps = {
    incrementalNumber: 0,
    shipment: {},
};

ShipmentHeader.propTypes = {
    incrementalNumber: PropTypes.number.isRequired,
    shipment: PropTypes.shape({
        shippedAt: PropTypes.string,
        shippingMethod:  PropTypes.shape({
            shippingMethodShopName: PropTypes.string
        }),
        trackingNumber: PropTypes.string,
        returnTrackingNumber: PropTypes.string,
        shipmentLabel: PropTypes.string,
        returnShipmentLabel: PropTypes.string,
        errorMessage: PropTypes.string,
        createdAt: PropTypes.string
    })
};

export default (withStyles(showStyle)(injectIntl(ShipmentHeader)));
